import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import type { Permission } from '../lib/auth'
import { Logo } from './Logo'
import { useAction } from './toast'
import { Avatar, Modal, Notice } from './ui'

type NavItem = { to: string; label: string; needs?: Permission[] }
type NavGroup = { eyebrow: string; items: NavItem[] }

const NAV: NavGroup[] = [
  {
    eyebrow: 'Operate',
    items: [
      { to: '/', label: 'Overview', needs: [P.adminRead] },
      { to: '/users', label: 'Users', needs: [P.userReadAny] },
      { to: '/emergency', label: 'Emergency events', needs: [P.emergencyReadAny] },
      { to: '/devices', label: 'Devices & sources', needs: [P.adminRead] },
      { to: '/care-links', label: 'Care links', needs: [P.userReadAny] },
    ],
  },
  {
    eyebrow: 'Intelligence',
    items: [
      { to: '/ai/usage', label: 'AI usage', needs: [P.aiUsageRead] },
      { to: '/ai/models', label: 'Model catalog', needs: [P.aiModelWrite] },
      { to: '/ai/routing', label: 'Routing rules', needs: [P.aiUsageRead] },
      { to: '/ai/agents', label: 'Agent flows', needs: [P.aiUsageRead] },
    ],
  },
  {
    eyebrow: 'Governance',
    items: [
      { to: '/privacy/policy', label: 'PHI policy', needs: [P.privacyPolicyWrite] },
      { to: '/privacy/requests', label: 'Consents & data rights', needs: [P.privacyPolicyWrite] },
      { to: '/roles', label: 'Roles & permissions', needs: [P.adminRead] },
      { to: '/audit', label: 'Audit log', needs: [P.auditRead] },
    ],
  },
  {
    eyebrow: 'Marketplace',
    items: [
      { to: '/coaches', label: 'Coaches', needs: [P.adminRead] },
      { to: '/payouts', label: 'Payouts', needs: [P.billingRead] },
      { to: '/payments/gateways', label: 'Payment gateways', needs: [P.billingRead] },
    ],
  },
  {
    eyebrow: 'Platform',
    items: [
      { to: '/billing', label: 'Subscriptions', needs: [P.billingRead] },
      { to: '/sign-in', label: 'Sign-in & keys', needs: [P.aiCredentialWrite] },
      { to: '/config', label: 'Content & config', needs: [P.adminRead] },
      { to: '/retention', label: 'Retention & archive', needs: [P.adminRead] },
      { to: '/system', label: 'System', needs: [P.adminRead] },
    ],
  },
]

function ThemeToggle() {
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (document.documentElement.dataset.theme as 'dark' | 'light') ?? 'dark',
  )

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('seyon.admin.theme', theme)
  }, [theme])

  return (
    <button
      className="btn btn--sm btn--ghost"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      title="Toggle theme"
    >
      {theme === 'dark' ? 'Dark' : 'Light'}
    </button>
  )
}

export function Shell() {
  const { user, can, signOut } = useAuth()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault()
    if (query.trim()) navigate(`/users?q=${encodeURIComponent(query.trim())}`)
  }

  const environment = import.meta.env.VITE_ENVIRONMENT ?? 'dev'

  return (
    <div className="shell">
      <nav className="rail" aria-label="Sections">
        <div className="rail__brand">
          <Logo height={24} />
        </div>

        {NAV.map((group) => {
          const visible = group.items.filter((item) => !item.needs || can(...item.needs))
          if (visible.length === 0) return null
          return (
            <div className="rail__group" key={group.eyebrow}>
              <div className="rail__eyebrow">{group.eyebrow}</div>
              {visible.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    isActive ? 'rail__link rail__link--active' : 'rail__link'
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          )
        })}

        <div className="spacer" />
        <div className="rail__eyebrow" style={{ paddingBottom: 0 }}>
          Signed in
        </div>
        <div className="row" style={{ padding: '0 10px' }}>
          <Avatar name={user?.display_name || user?.email || '?'} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.display_name || user?.email}
            </div>
            <div className="dim" style={{ fontSize: 10.5 }}>
              {user?.roles.join(' · ')}
            </div>
          </div>
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <form className="topbar__search" onSubmit={submitSearch}>
            <input
              className="input"
              placeholder="Search users by email…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search users"
            />
          </form>
          <div className="spacer" />
          <span
            className={
              environment === 'prod' ? 'badge badge--alert' : 'badge badge--cyan'
            }
            title="Which deployment this console is pointed at"
          >
            {environment}
          </span>
          <ThemeToggle />
          <button className="btn btn--sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </header>
        {user?.must_change_password && <TemporaryPasswordBanner />}
        <Outlet />
      </div>
    </div>
  )
}

/**
 * Shown while the account is still on the password an admin generated for it.
 *
 * Deliberately a prompt rather than a lock: someone who cannot reach the console
 * until they change a password they may not have on hand is someone who cannot
 * do the job they were just given an account for. The banner persists until the
 * change happens, which is what retires the operator's copy.
 */
function TemporaryPasswordBanner() {
  const [changing, setChanging] = useState(false)

  return (
    <>
      <div style={{ padding: '12px 20px 0' }}>
        <Notice tone="warn">
          <span className="row" style={{ gap: 10 }}>
            <span>
              This account is still using the temporary password it was created with. Until you
              replace it, whoever set it up can sign in as you.
            </span>
            <button className="btn btn--sm" onClick={() => setChanging(true)}>
              Change password
            </button>
          </span>
        </Notice>
      </div>
      {changing && <ChangePassword onClose={() => setChanging(false)} />}
    </>
  )
}

function ChangePassword({ onClose }: { onClose: () => void }) {
  const run = useAction()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const tooShort = next.length > 0 && next.length < 12
  const mismatch = confirm.length > 0 && confirm !== next
  const canSave = current.length > 0 && next.length >= 12 && confirm === next

  const save = async () => {
    setBusy(true)
    await run(
      () => post('/v1/me/password', { current_password: current, new_password: next }),
      'Password changed. Sign in again on your other devices.',
      () => {
        onClose()
        // The banner is driven by the session payload, which only refreshes on a
        // new sign-in. Reloading is the shortest honest way to reflect the change.
        window.location.reload()
      },
    )
    setBusy(false)
  }

  return (
    <Modal title="Change your password" onClose={onClose}>
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="cur-pw">
            Current password
          </label>
          <input
            id="cur-pw"
            className="input"
            type="password"
            autoComplete="current-password"
            value={current}
            autoFocus
            onChange={(event) => setCurrent(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="new-pw">
            New password (at least 12 characters)
          </label>
          <input
            id="new-pw"
            className="input"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
          {tooShort && <p className="muted" style={{ margin: '4px 0 0' }}>Too short.</p>}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="confirm-pw">
            Confirm new password
          </label>
          <input
            id="confirm-pw"
            className="input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
          {mismatch && <p className="muted" style={{ margin: '4px 0 0' }}>These do not match.</p>}
        </div>
        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={busy || !canSave} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
